/**
 * Confirm a purchase into local Dexie + outbox (Task 7).
 * One Dexie transaction. Purchases never write observations (docs/04 §0).
 * Outbox FK order: merchants → purchase_queries → recommendation_results →
 * transactions → transaction_splits → cash_reservations (docs/08 §4).
 * reward_cap_periods is local-only — updated here, never outboxed.
 */

import type { CardZappDB } from '../db/database';
import { enqueueOutbox } from '../db/outbox';
import { payloadHash } from '../db/canonicalize';
import type { Cents } from '../domain/money';
import { asCents } from '../domain/money';
import type { BusinessDate } from '../domain/time';
import type {
  ConfidenceTier,
  RankCardsInput,
  RecommendationOutput,
  SpendCategory,
} from '../domain/recommend/types';
import type {
  CashReservationLocal,
  MerchantLocal,
  PurchaseQueryLocal,
  RecommendationResultLocal,
  TransactionLocal,
  TransactionSplitLocal,
} from '../schemas/entities';
import type { RewardCapPeriod } from '../schemas/local-only';

export type ConfirmPurchaseInput = {
  db: CardZappDB;
  /** Merchant typed on the query. Normalized for lookup/create. */
  merchant_input: string;
  /** Existing merchant id, or null to create. */
  merchant_id: string | null;
  /** Optional Item(s) text stored on purchase_query + transaction.description. */
  description?: string | null;
  amount_cents: Cents;
  /** Exact total confirmed by the user (may differ from the query amount). */
  exact_total_cents: Cents;
  transaction_date: BusinessDate;
  /** Card actually used — defaults to recommended in the UI, always overridable. */
  card_id_used: string;
  checking_account_label: string;
  rank_input: RankCardsInput;
  recommendation: RecommendationOutput;
  rules_version: string;
  /** ISO timestamptz for created_at / entered_at / generated_at. */
  now_iso: string;
};

export type ConfirmPurchaseResult = {
  merchant_id: string;
  purchase_query_id: string;
  recommendation_result_id: string;
  transaction_id: string;
  split_ids: readonly string[];
  cash_reservation_id: string;
  outbox_tables: readonly string[];
};

function normalizeMerchantName(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, ' ');
}

function stripLocal(row: object): Record<string, unknown> {
  const src = row as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(src)) {
    if (key === '_localDirty' || key === '_baseServerUpdatedAt') continue;
    out[key] = src[key];
  }
  return out;
}

function winnerConfidence(
  recommendation: RecommendationOutput,
  rankInput: RankCardsInput,
): ConfidenceTier {
  const winnerId = recommendation.winner_card_id;
  if (winnerId === null) return 'unverified';
  const ranked = recommendation.eligible.find((c) => c.card_id === winnerId);
  if (ranked === undefined) return 'unverified';
  const ruleId = ranked.trace.matched_bonus_rule_id ?? ranked.trace.matched_base_rule_id;
  if (ruleId === null) return 'unverified';
  const rule = rankInput.rewardRules.find((r) => r.id === ruleId);
  return rule?.confidence ?? 'unverified';
}

function categorySourceForTxn(
  recommendation: RecommendationOutput,
): 'user' | 'merchant_default' | 'observed' | 'unknown' {
  return recommendation.category_source;
}

function buildSplits(args: {
  transactionId: string;
  amount: Cents;
  category: SpendCategory;
  recommendation: RecommendationOutput;
  cardIdUsed: string;
  rankInput: RankCardsInput;
  nowIso: string;
}): TransactionSplitLocal[] {
  const winner = args.recommendation.eligible.find((c) => c.card_id === args.cardIdUsed);
  const trace = winner?.trace;
  const bonusRuleId = trace?.matched_bonus_rule_id ?? null;
  const baseRuleId = trace?.matched_base_rule_id ?? null;
  const capPartial = winner?.gate_failures.some((g) => g.code === 'CAP_PARTIAL') === true;

  if (capPartial && bonusRuleId !== null) {
    const cap = args.rankInput.capPeriods.find((c) => c.reward_rule_id === bonusRuleId);
    const headroom = cap?.remaining_cap_cents ?? asCents(0n);
    const head = headroom < args.amount ? headroom : args.amount;
    const rest = asCents(args.amount - head);
    const splits: TransactionSplitLocal[] = [];
    if (head > 0n) {
      splits.push({
        id: crypto.randomUUID(),
        user_id: null,
        transaction_id: args.transactionId,
        amount_cents: head,
        category: args.category,
        reward_rule_id: bonusRuleId,
        expected_reward_cents: null,
        notes: null,
        created_at: args.nowIso,
        updated_at: args.nowIso,
        deleted_at: null,
        _localDirty: true,
        _baseServerUpdatedAt: null,
      });
    }
    if (rest > 0n) {
      splits.push({
        id: crypto.randomUUID(),
        user_id: null,
        transaction_id: args.transactionId,
        amount_cents: rest,
        category: args.category,
        reward_rule_id: baseRuleId,
        expected_reward_cents: null,
        notes: null,
        created_at: args.nowIso,
        updated_at: args.nowIso,
        deleted_at: null,
        _localDirty: true,
        _baseServerUpdatedAt: null,
      });
    }
    return splits;
  }

  return [
    {
      id: crypto.randomUUID(),
      user_id: null,
      transaction_id: args.transactionId,
      amount_cents: args.amount,
      category: args.category,
      reward_rule_id: bonusRuleId ?? baseRuleId,
      expected_reward_cents: trace?.discounted_reward_cents ?? null,
      notes: null,
      created_at: args.nowIso,
      updated_at: args.nowIso,
      deleted_at: null,
      _localDirty: true,
      _baseServerUpdatedAt: null,
    },
  ];
}

async function applyCapProgress(
  db: CardZappDB,
  cardIdUsed: string,
  recommendation: RecommendationOutput,
  amount: Cents,
): Promise<void> {
  const ranked = recommendation.eligible.find((c) => c.card_id === cardIdUsed);
  if (ranked === undefined) return;
  const ruleId = ranked.trace.matched_bonus_rule_id ?? ranked.trace.matched_base_rule_id;
  if (ruleId === null) return;

  const existing = await db.reward_cap_periods.toArray();
  const row = existing.find((r) => {
    const rec = r as RewardCapPeriod & { reward_rule_id?: string };
    return rec.reward_rule_id === ruleId;
  }) as
    | (RewardCapPeriod & { reward_rule_id?: string; remaining_cap_cents?: number | bigint })
    | undefined;

  if (row === undefined) return;

  const remainingRaw = row.remaining_cap_cents;
  if (remainingRaw === undefined) return;
  const remaining = typeof remainingRaw === 'bigint' ? remainingRaw : BigInt(remainingRaw);
  const next = remaining > amount ? remaining - amount : 0n;
  const updated: RewardCapPeriod = {
    ...row,
    reward_rule_id: ruleId,
    remaining_cap_cents: next,
  };
  await db.reward_cap_periods.put(updated);
}

/**
 * Persist a confirmed purchase. Network is never awaited.
 */
export async function confirmPurchase(input: ConfirmPurchaseInput): Promise<ConfirmPurchaseResult> {
  const { db, now_iso: now } = input;
  const query = input.rank_input.query;
  const category = input.recommendation.category_resolved;
  const recommendedId = input.recommendation.winner_card_id;

  const purchaseQueryId = crypto.randomUUID();
  const recommendationResultId = crypto.randomUUID();
  const transactionId = crypto.randomUUID();
  const cashReservationId = crypto.randomUUID();

  let merchantId = input.merchant_id;
  let merchantRow: MerchantLocal | null = null;
  let createdMerchant = false;

  if (merchantId === null) {
    merchantId = crypto.randomUUID();
    createdMerchant = true;
    merchantRow = {
      id: merchantId,
      user_id: null,
      normalized_name: normalizeMerchantName(input.merchant_input),
      raw_names: [input.merchant_input.trim()],
      default_category: null,
      online_category: null,
      in_store_category: null,
      possible_mccs: [],
      category_confidence: 'unverified',
      is_intermediary: false,
      known_exceptions: null,
      parent_merchant_id: null,
      last_verified_at: null,
      created_at: now,
      updated_at: now,
      deleted_at: null,
      _localDirty: true,
      _baseServerUpdatedAt: null,
    };
  }

  const descriptionRaw = input.description ?? null;
  const descriptionTrimmed = descriptionRaw === null ? '' : descriptionRaw.trim();
  const description = descriptionTrimmed === '' ? null : descriptionTrimmed;

  const purchaseQuery: PurchaseQueryLocal = {
    id: purchaseQueryId,
    user_id: null,
    merchant_input: input.merchant_input.trim(),
    merchant_id: merchantId,
    description,
    category: query.category,
    amount_cents: input.amount_cents,
    channel: query.channel,
    geography: query.geography,
    currency: 'USD',
    recurring: query.recurring,
    owner: query.owner,
    financing_requested: query.financing_requested,
    return_risk: query.return_risk,
    mode: query.mode,
    pay_before_statement_close: query.pay_before_statement_close,
    allow_interest_bearing: query.allow_interest_bearing,
    entered_at: now,
    created_at: now,
    updated_at: now,
    deleted_at: null,
    _localDirty: true,
    _baseServerUpdatedAt: null,
  };

  const winner = input.recommendation.eligible.find((c) => c.card_id === recommendedId);
  const alt = input.recommendation.eligible.find((c) => c.card_id !== recommendedId);
  const inputSnapshot = {
    query: input.rank_input.query,
    asOf: input.rank_input.asOf,
    rules_version: input.rules_version,
    winner_card_id: recommendedId,
    card_id_used: input.card_id_used,
  };
  const fingerprint = await payloadHash(inputSnapshot);

  const recommendationResult: RecommendationResultLocal = {
    id: recommendationResultId,
    user_id: null,
    purchase_query_id: purchaseQueryId,
    rules_version: input.rules_version,
    input_snapshot: inputSnapshot,
    input_fingerprint: fingerprint,
    recommended_card_id: recommendedId,
    alternative_card_id: alt?.card_id ?? null,
    no_eligible_card: input.recommendation.no_eligible_card,
    ranked: {
      eligible: input.recommendation.eligible.map((c) => c.card_id),
      ineligible: input.recommendation.ineligible.map((c) => c.card_id),
    },
    gates_failed: Object.fromEntries(
      [...input.recommendation.eligible, ...input.recommendation.ineligible].map((c) => [
        c.card_id,
        c.gate_failures,
      ]),
    ),
    discounted_reward_cents: winner?.trace.discounted_reward_cents ?? null,
    discounted_offer_cents: winner?.trace.discounted_offer_cents ?? null,
    fx_fee_cents: winner?.trace.fx_fee_cents ?? null,
    interest_risk_cents: winner?.trace.interest_risk_cents ?? null,
    expected_net_cents: winner?.trace.expected_net_cents ?? null,
    utilization_after_bps: winner?.trace.post_purchase_utilization_bps ?? null,
    confidence: winnerConfidence(input.recommendation, input.rank_input),
    explanation: winner ? [...winner.explanation] : [],
    warnings: [...input.recommendation.warnings],
    generated_at: now,
    created_at: now,
    updated_at: now,
    deleted_at: null,
    _localDirty: true,
    _baseServerUpdatedAt: null,
  };

  const usedTrace = input.recommendation.eligible.find(
    (c) => c.card_id === input.card_id_used,
  )?.trace;

  const transaction: TransactionLocal = {
    id: transactionId,
    user_id: null,
    card_id: input.card_id_used,
    merchant_name_raw: input.merchant_input.trim(),
    merchant_id: merchantId,
    description,
    amount_cents: input.exact_total_cents,
    currency: 'USD',
    original_amount_cents: null,
    fx_rate_micros: null,
    kind: 'purchase',
    transaction_date: input.transaction_date,
    posted_date: null,
    status: 'pending',
    category: category === 'unknown' ? null : category,
    category_src: categorySourceForTxn(input.recommendation),
    owner: query.owner,
    channel: query.channel,
    geography: query.geography,
    recurring_charge_id: null,
    statement_cycle_id: null,
    assignment: 'unassigned',
    payment_state: 'unassigned',
    expected_reward_rate_bps: null,
    expected_reward_cents: usedTrace?.discounted_reward_cents ?? null,
    actual_reward_cents: null,
    reward_rule_id: usedTrace?.matched_bonus_rule_id ?? usedTrace?.matched_base_rule_id ?? null,
    offer_id: null,
    recommendation_result_id: recommendationResultId,
    recommended_card_id: recommendedId,
    refunds_transaction_id: null,
    receipt_reference: null,
    notes: null,
    created_at: now,
    updated_at: now,
    deleted_at: null,
    _localDirty: true,
    _baseServerUpdatedAt: null,
  };

  const splits = buildSplits({
    transactionId,
    amount: input.exact_total_cents,
    category,
    recommendation: input.recommendation,
    cardIdUsed: input.card_id_used,
    rankInput: input.rank_input,
    nowIso: now,
  });

  const cashReservation: CashReservationLocal = {
    id: cashReservationId,
    user_id: null,
    transaction_id: transactionId,
    card_id: input.card_id_used,
    amount_reserved_cents: input.exact_total_cents,
    checking_account_label: input.checking_account_label,
    reservation_date: input.transaction_date,
    released_date: null,
    status: 'reserved',
    created_at: now,
    updated_at: now,
    deleted_at: null,
    _localDirty: true,
    _baseServerUpdatedAt: null,
  };

  const outboxTables: string[] = [];

  const merchantPayload = merchantRow !== null ? stripLocal(merchantRow) : null;
  const purchasePayload = stripLocal(purchaseQuery);
  const recommendationPayload = stripLocal(recommendationResult);
  const transactionPayload = stripLocal(transaction);
  const splitPayloads: { id: string; payload: Record<string, unknown> }[] = splits.map((s) => ({
    id: String(s.id),
    payload: stripLocal(s),
  }));
  const cashPayload = stripLocal(cashReservation);

  const merchantHash: string = merchantPayload !== null ? await payloadHash(merchantPayload) : '';
  const purchaseHash: string = await payloadHash(purchasePayload);
  const recommendationHash: string = await payloadHash(recommendationPayload);
  const transactionHash: string = await payloadHash(transactionPayload);
  const splitHashes: string[] = [];
  for (const sp of splitPayloads) {
    splitHashes.push(await payloadHash(sp.payload));
  }
  const cashHash: string = await payloadHash(cashPayload);

  const tables = [
    db.merchants,
    db.purchase_queries,
    db.recommendation_results,
    db.transactions,
    db.transaction_splits,
    db.cash_reservations,
    db.reward_cap_periods,
    db.outbox,
  ] as const;

  await db.transaction('rw', [...tables], async () => {
    if (createdMerchant && merchantRow !== null && merchantPayload !== null) {
      await db.merchants.put(merchantRow);
      await enqueueOutbox(db, {
        table: 'merchants',
        op: 'insert',
        rowId: merchantId,
        payload: merchantPayload,
        payloadHash: merchantHash,
        baseServerUpdatedAt: null,
        createdAt: now,
      });
      outboxTables.push('merchants');
    }

    await db.purchase_queries.put(purchaseQuery);
    await enqueueOutbox(db, {
      table: 'purchase_queries',
      op: 'insert',
      rowId: purchaseQueryId,
      payload: purchasePayload,
      payloadHash: purchaseHash,
      baseServerUpdatedAt: null,
      createdAt: now,
    });
    outboxTables.push('purchase_queries');

    await db.recommendation_results.put(recommendationResult);
    await enqueueOutbox(db, {
      table: 'recommendation_results',
      op: 'insert',
      rowId: recommendationResultId,
      payload: recommendationPayload,
      payloadHash: recommendationHash,
      baseServerUpdatedAt: null,
      createdAt: now,
    });
    outboxTables.push('recommendation_results');

    await db.transactions.put(transaction);
    await enqueueOutbox(db, {
      table: 'transactions',
      op: 'insert',
      rowId: transactionId,
      payload: transactionPayload,
      payloadHash: transactionHash,
      baseServerUpdatedAt: null,
      createdAt: now,
    });
    outboxTables.push('transactions');

    for (let i = 0; i < splits.length; i++) {
      const split = splits[i];
      const hashed = splitHashes[i];
      const sp = splitPayloads[i];
      if (split === undefined || hashed === undefined || sp === undefined) {
        throw new Error('confirmPurchase: split hash mismatch');
      }
      await db.transaction_splits.put(split);
      await enqueueOutbox(db, {
        table: 'transaction_splits',
        op: 'insert',
        rowId: sp.id,
        payload: sp.payload,
        payloadHash: hashed,
        baseServerUpdatedAt: null,
        createdAt: now,
      });
      outboxTables.push('transaction_splits');
    }

    await db.cash_reservations.put(cashReservation);
    await enqueueOutbox(db, {
      table: 'cash_reservations',
      op: 'insert',
      rowId: cashReservationId,
      payload: cashPayload,
      payloadHash: cashHash,
      baseServerUpdatedAt: null,
      createdAt: now,
    });
    outboxTables.push('cash_reservations');

    await applyCapProgress(db, input.card_id_used, input.recommendation, input.exact_total_cents);
  });

  return {
    merchant_id: merchantId,
    purchase_query_id: purchaseQueryId,
    recommendation_result_id: recommendationResultId,
    transaction_id: transactionId,
    split_ids: splits.map((s) => String(s.id)),
    cash_reservation_id: cashReservationId,
    outbox_tables: outboxTables,
  };
}
